import { useEffect, useRef, useState } from 'react';
import { ENewNotificationType } from '@enum';
import { useNotificationsContext } from '@hooks/context/useNotificationsContext';

interface IProps<T, R> {
    method: (data?: T) => Promise<R>;
    data?: T;
    isInitialRequest?: boolean;
    deps?: any[];
    onSuccess?: (data: R, requestData?: T) => void;
    onError?: (error: any) => void;
    onFinish?: () => void;
    condition?: boolean;
    isInitialLoading?: boolean;
    notificationType?: ENewNotificationType;
    // Opt-in reliability for lists whose rows change status server-side (e.g. a
    // money request a contact approves on another device). The Firebase
    // notification refetch is best-effort; these guarantee the view reconciles
    // without a manual page refresh. Both are gated by `condition` and only run
    // while the tab is visible. Off by default — no effect on existing callers.
    refetchOnFocus?: boolean;
    refetchInterval?: number;
}

export const useApiQuery = <T, R>({
    method,
    deps = [],
    isInitialRequest: isInitial = true,
    data: requestData,
    condition = true,
    isInitialLoading = false,
    onError,
    onSuccess,
    onFinish,
    notificationType,
    refetchOnFocus = false,
    refetchInterval
}: IProps<T, R>) => {
    const [isInitialRequest, setIsInitialRequest] = useState(isInitial);
    const [response, setResponse] = useState<R | undefined>(undefined);
    const [error, setError] = useState<string>();
    const [isLoading, setLoading] = useState(isInitialLoading);
    const { newNotificationType } = useNotificationsContext();

    // Generation counter — guards against request sequencing races: a
    // superseded request (e.g. a `deps` value like the dev portal's
    // Sandbox/Live pill changes and re-fires before the prior call settles)
    // must not apply its result if a newer request has since been fired.
    // Incremented per call; captured locally so a request can tell, once its
    // promise settles, whether it is still the most recent one.
    const requestGenerationRef = useRef(0);

    const handleRequest = async (requestData?: T) => {
        const generation = ++requestGenerationRef.current;
        setLoading(true);
        setError(undefined);
        method(requestData)
            .then((result) => {
                if (generation !== requestGenerationRef.current) return;
                setLoading(false);
                setResponse(result);
                if (onSuccess) onSuccess(result, requestData);
            })
            .catch((error) => {
                if (generation !== requestGenerationRef.current) return;
                setLoading(false);
                setError(error?.errorCode || '');
                if (onError) onError(error);
            })
            .finally(() => {
                if (generation !== requestGenerationRef.current) return;
                setLoading(false);
                if (onFinish) onFinish();
            });
    };

    useEffect(() => {
        if (isInitialRequest) {
            if (condition) {
                handleRequest(requestData);
            }
        }

        setIsInitialRequest(true);
    }, deps);

    useEffect(() => {
        if (notificationType && notificationType === newNotificationType) {
            if (condition) {
                setTimeout(() => {
                    handleRequest(requestData);
                }, 2000);
            }
        }
    }, [newNotificationType, notificationType]);

    // Keep refs to the freshest request fn and data so the listeners/timers
    // below never call a stale closure and never need to be re-created on every
    // render. lastRefetchAtRef debounces bursts (e.g. visibilitychange + focus
    // firing together on tab return) into a single request.
    const latestRequestRef = useRef(handleRequest);
    latestRequestRef.current = handleRequest;
    const latestDataRef = useRef(requestData);
    latestDataRef.current = requestData;
    const lastRefetchAtRef = useRef(0);

    const guardedRefetch = () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - lastRefetchAtRef.current < 1000) return; // collapse bursts
        lastRefetchAtRef.current = now;
        latestRequestRef.current(latestDataRef.current);
    };

    // Reconcile the view when the tab regains focus — this is what the user does
    // manually today (refresh after a contact approves on another device).
    useEffect(() => {
        if (!refetchOnFocus) return;
        const onFocus = () => {
            if (condition) guardedRefetch();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [refetchOnFocus, condition]);

    // Poll the active list so server-side status changes reconcile without any
    // user action. Only runs while the tab is visible to avoid background churn.
    useEffect(() => {
        if (!refetchInterval || !condition) return;
        const id = setInterval(() => {
            if (condition) guardedRefetch();
        }, refetchInterval);
        return () => clearInterval(id);
    }, [refetchInterval, condition]);

    return {
        data: response,
        error,
        isLoading,
        handleRequest,
        removeResponse: () => setResponse(undefined)
    };
};
